import {
  buildDataTableCols,
  createCheckboxPreset,
  type DataTableCol,
  type DataTableColumn
} from '@/components/custom/erp/data-table-v2';
import {
  buildInputTableCols,
  ConversionQuantityFieldV2,
  DvtFieldV2,
  type InputTableCol
} from '@/components/custom/erp';
import type { SelectSearchOption } from '@/components/custom/erp/data-table-v2/search-inputs';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';
import type { ChiTietPhieuXuatKho, PhieuXuatKho } from '@/types/schemas';
import { Checkbox } from '@/components/ui/checkbox';

type PhieuXuatKhoRow = PhieuXuatKho;

const STATUS_OPTIONS: SelectSearchOption[] = [
  { value: '0', label: 'Lập chứng từ' },
  { value: '3', label: 'Chờ duyệt' },
  { value: '5', label: 'Xuất kho' },
  { value: '6', label: 'Đang thực hiện' },
  { value: '7', label: 'Hoàn thành' },
  { value: '8', label: 'Xuất hóa đơn' },
  { value: '9', label: 'Hủy' }
];

export const BASE_COLS: DataTableCol<PhieuXuatKhoRow>[] = [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    valueGetter: row => row?.status,
    valueFormatter: value => STATUS_OPTIONS.find(option => option.value === String(value ?? ''))?.label ?? '',
    filter: { type: 'multiple', options: STATUS_OPTIONS }
  },
  { field: 'so_ct', headerName: 'Số phiếu xuất', width: 150, valueGetter: row => row?.so_ct },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 120,
    valueGetter: row => row?.ngay_ct,
    valueFormatter: value => formatDate(String(value ?? '')),
    filter: { type: 'date' }
  },
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 140,
    valueGetter: row => row?.ma_kh_data?.customer_code || row?.ma_kh || ''
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 220,
    valueGetter: row => row?.ma_kh_data?.customer_name || row?.ten_kh || ''
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 220,
    valueGetter: row => row?.dien_giai
  },
  {
    field: 't_tien_nt',
    headerName: 'Tổng tiền',
    width: 150,
    columnType: 'number',
    align: 'right',
    valueGetter: row => row?.t_tien_nt,
    valueFormatter: value => formatRoundedNumber(Number(value ?? 0)),
    filter: { type: 'text' }
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 120,
    valueGetter: row => row?.ma_nt_data?.ma_nt || row?.ma_nt || ''
  },
  {
    field: 't_so_luong',
    headerName: 'Tổng số lượng',
    width: 140,
    columnType: 'number',
    align: 'right',
    valueGetter: row => row?.t_so_luong,
    valueFormatter: value => formatMoney(Number(value ?? 0))
  },
  {
    field: 'ma_nk',
    headerName: 'Mã CT',
    width: 100,
    valueGetter: row => row?.ma_nk_data?.ma_nk || ''
  }
];

export const getDataTableColumns = (
  handleViewClick: (obj: PhieuXuatKho) => void,
  totalAmount?: number | null,
  isTotalLoading?: boolean
): DataTableColumn[] => {
  const total = (
    <div className='flex items-center gap-2'>
      <span className='text-lg font-bold'>&#931;</span>
      <span className='font-semibold text-main'>
        {isTotalLoading ? 'Đang tính...' : typeof totalAmount === 'number' ? formatRoundedNumber(totalAmount) : '—'}
      </span>
    </div>
  );

  const cols = BASE_COLS.map(col => {
    if (col.field === 'so_ct') {
      return {
        ...col,
        renderCell: params => (
          <button
            onClick={event => {
              event.stopPropagation();
              handleViewClick(params.row);
            }}
            className='hover:text-blue-500 hover:underline'
          >
            {params.row?.so_ct}
          </button>
        )
      };
    }

    if (col.field === 't_tien_nt') {
      return {
        ...col,
        filter: { type: 'text' as const, after: total }
      };
    }

    return col;
  }) as DataTableCol<PhieuXuatKhoRow>[];

  return buildDataTableCols<PhieuXuatKhoRow>(cols);
};

export const exportMainColumns = (
  handleViewClick: (row: PhieuXuatKho) => void,
  totalAmount?: number | null,
  isTotalLoading?: boolean
): DataTableColumn<PhieuXuatKho>[] => [
  {
    field: 'checkbox',
    headerName: '',
    width: 50,
    align: 'center',
    preset: createCheckboxPreset({
      getCheckedValue: row => Boolean(row?.checkbox),
      renderCheckbox: checked => <Checkbox checked={checked} />
    })
  },
  ...getDataTableColumns(handleViewClick, totalAmount, isTotalLoading)
];

export const INPUT_TABLE_BASE_COLS: InputTableCol<ChiTietPhieuXuatKho>[] = [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 150,
    renderCell: params => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: params => params.row.ma_vt_data?.ten_vt
  },
  {
    field: 'ma_dvt',
    headerName: 'Đvt',
    width: 80,
    renderCell: params => <DvtFieldV2 row={params.row} />
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 100,
    renderCell: params => params.row.ma_kho_data?.ma_kho
  },
  {
    field: 'ton_kho',
    headerName: 'Tồn',
    width: 100,
    renderCell: params => params.row.ma_vt_data?.ton_kho
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 150,
    renderCell: params => <ConversionQuantityFieldV2 row={params.row} />
  },
  {
    field: 'dich_danh',
    headerName: 'Đích danh',
    width: 100,
    columnType: 'boolean',
    align: 'center',
    renderCell: params => <Checkbox checked={params.row.px_dd} disabled />
  },
  {
    field: 'don_gia',
    headerName: 'Giá VND',
    width: 130,
    renderCell: params => formatMoney(Number(params.row.gia ?? 0))
  },
  {
    field: 'thanh_tien',
    headerName: 'Tiền VND',
    width: 160,
    renderCell: params => formatMoney(Number(params.row.tien ?? 0))
  },
  { field: 'loai_ts_cc', headerName: 'Loại TS/CC', width: 120 },
  {
    field: 'tk_co',
    headerName: 'Tk có',
    width: 100,
    renderCell: params => params.row.tk_vt_data?.code
  },
  {
    field: 'ly_do_xuat',
    headerName: 'Lý do xuất',
    width: 120,
    renderCell: params => params.row.ma_nx_data?.ma_nx
  },
  {
    field: 'tk_no',
    headerName: 'Tk nợ',
    width: 100,
    renderCell: params => params.row.tk_no_data?.code || params.row.tk_du_data?.code
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: params => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vu_viec',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: params => params.row.ma_vv_data?.ma_vv
  },
  {
    field: 'dot_tt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: params => params.row.ma_tt_data?.ma_tt
  },
  {
    field: 'ma_hd',
    headerName: 'Khế ước',
    width: 120,
    renderCell: params => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'phi',
    headerName: 'Phí',
    width: 100,
    renderCell: params => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ma_lenh_sx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: params => params.row.ma_lsx_data?.so_ct
  },
  {
    field: 'so_phieu_yc',
    headerName: 'Số phiếu YC',
    width: 120
  },
  { field: 'dong_yc', headerName: 'Dòng YC', width: 120 }
];

export const INPUT_TABLE_COLUMNS = buildInputTableCols(INPUT_TABLE_BASE_COLS);
export const warehouseExportDetailColumns = INPUT_TABLE_COLUMNS;

export const historyColumns: DataTableColumn[] = [];
